/**
 * SignUp: email + password + display name. Nothing else — the account is
 * free and takes thirty seconds.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { registerAccount } from '../lib/auth';
import { AuthField, AuthShell, AuthSubmit } from '../components/AuthShell';

function errorMessage(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === 'rate_limited') return t('auth.rateLimited');
    return error.message;
  }
  return error instanceof Error ? error.message : t('auth.signUpFailed');
}

export function SignUp(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await registerAccount(email.trim(), password, displayName.trim(), i18n.language);
      navigate('/signin', { state: { registered: true, email: email.trim() } });
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={t('auth.signUpTitle')}
      subtitle={t('auth.signUpSubtitle')}
    >
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <AuthField
          label={t('auth.displayName')}
          type="text"
          required
          maxLength={120}
          autoComplete="nickname"
          placeholder={t('auth.displayNamePlaceholder')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <AuthField
          label={t('auth.email')}
          type="email"
          required
          autoComplete="email"
          placeholder={t('auth.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <AuthField
          label={t('auth.password')}
          type="password"
          required
          autoComplete="new-password"
          placeholder={t('auth.passwordPlaceholder')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <p className="-mt-2 text-xs text-stone-500">
          {t('auth.passwordHint')}
        </p>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <AuthSubmit busy={busy} busyLabel={t('auth.creating')}>
          {t('auth.createAccount')}
        </AuthSubmit>
      </form>
      <p className="mt-4 text-center text-sm text-stone-600">
        {t('auth.haveAccount')}{' '}
        <Link to="/signin" className="font-medium text-tenant-primary hover:underline">
          {t('auth.signInButton')}
        </Link>
      </p>
    </AuthShell>
  );
}
